import { Type } from '@angular/core';
import { PortfolioBaseType, PortfolioComponentId } from '@types';
import { DocumentNode } from '@apollo/client/core';
import assetAllocationQuery from '@graphql/portfolio/asset-allocation.graphql';
import currencyAllocationQuery from '@graphql/portfolio/currency-allocation.graphql';
import holdingsQuery from '@graphql/portfolio/holdings.graphql';
import couponAllocationQuery from '@graphql/portfolio/coupon-allocation.graphql';
import geographicAllocationQuery from '@graphql/portfolio/geographic-allocation.graphql';
import hedgeAllocationQuery from '@graphql/portfolio/hedge-allocation.graphql';
import impactPillarQuery from '@graphql/portfolio/impact-pillar.graphql';
import marketCapAllocationQuery from '@graphql/portfolio/market-cap-allocation.graphql';
import managerRosterAllocationQuery from '@graphql/portfolio/manager-roster-allocation.graphql';
import portfolioAllocationFIQuery from '@graphql/portfolio/fi-allocation.graphql';
import qualityAllocationQuery from '@graphql/portfolio/quality-allocation.graphql';
import riskReturnAnalysisQuery from '@graphql/portfolio/risk-return-analysis.graphql';
import sectorAllocationQuery from '@graphql/portfolio/sector-allocation.graphql';
import securityExposureQuery from '@graphql/portfolio/security-exposure.graphql';
import statisticsQuery from '@graphql/portfolio/statistics.graphql';
import topExposureQuery from '@graphql/portfolio/top-exposure.graphql';
import topHoldingsQuery from '@graphql/portfolio/top-holdings.graphql';
import topTenHoldingsMoneyFundsQuery from '@graphql/portfolio/top-ten-holdings.graphql';
import impactFocusQuery from '@graphql/portfolio/impact-focus.graphql';
import { Mapper } from '@products/utils/mappers/type.mapper';
import { AssetAllocationMapper } from '@products/utils/mappers/portfolio/asset-allocation.mapper';
import { GeographicAllocationMapper } from '@products/utils/mappers/portfolio/geographic-allocation.mapper';
import { SectorAllocationMapper } from '@products/utils/mappers/portfolio/sector-allocation.mapper';
import { TopHoldingsMapper } from '@products/utils/mappers/portfolio/topholdings.mapper';
import { PortfolioHoldingsComponent } from './portfolio-holdings/portfolio-holdings.component';
import { HoldingsMapper } from '@products/utils/mappers/portfolio/holdings.mapper';
import { StatisticsAndPositionsComponent } from './statistics-and-positions/statistics-and-positions.component';
import { StatisticMapper } from '@products/utils/mappers/portfolio/statistic.mapper';
import { MarketCapitalisationMapper } from '@products/utils/mappers/portfolio/market-capitalisation.mapper';
import { CurrencyAllocationMapper } from '@products/utils/mappers/portfolio/currency-allocation.mapper';
import { TopExposureMapper } from '@products/utils/mappers/portfolio/topexposure.mapper';
import { RiskReturnAnalysisMapper } from '@products/utils/mappers/portfolio/risk-and-return-analysis.mapper';
import { PortfolioAllocationFIMapper } from '@products/utils/mappers/portfolio/portfolio-allocation-fi.mapper';
import { QualityAllocationMapper } from '@products/utils/mappers/portfolio/quality-allocation.mapper';
import { SecurityExposureMapper } from '@products/utils/mappers/portfolio/security-exposure.mapper';
import { ManagerRosterComponent } from './manager-roster/manager-roster.component';
import { ManagerAllocationMapper } from '@products/utils/mappers/portfolio/manager-allocation.mapper';
import { TopTenHoldingsMoneyFundsMapper } from '@products/utils/mappers/portfolio/topTenHoldingsMoneyFunds.mapper';
import { CouponAllocationMapper } from '@products/utils/mappers/portfolio/coupon-allocation.mapper';
import { PortfolioChartsComponent } from './portfolio-charts/portfolio-charts.component';
import { HedgeAllocationMapper } from '@products/utils/mappers/portfolio/hedge-allocation.mapper';
import { ImpactPillarMapper } from '@products/utils/mappers/portfolio/impact-pillar.mapper';
import { ImpactFocusMapper } from '@products/utils/mappers/portfolio/impact-focus.mapper';

export class PortfolioComponentConfig {
  // private to disallow creating other instances of this type
  private constructor(
    public readonly componentId: PortfolioComponentId,
    public readonly component: Type<any>,
    public readonly baseType: PortfolioBaseType,
    public readonly gqlQuery: DocumentNode,
    // any is used because PortfolioComponentConfig is very generic to be used with different mapper for components
    public readonly mapper: Type<Mapper<any>>,
    // used for labels, caveats etc e.g. "top-ten-holding-money-funds"
    public readonly baseLabelKey?: string
  ) {}
  // config class based on this https://stackoverflow.com/a/51398471

  // chart components
  static readonly ASSET_ALLOCATION = new PortfolioComponentConfig(
    PortfolioComponentId.ASSET_ALLOCATION,
    PortfolioChartsComponent,
    PortfolioBaseType.PORTFOLIO_CHART,
    assetAllocationQuery,
    AssetAllocationMapper
  );

  static readonly IMPACT_PILLAR = new PortfolioComponentConfig(
    PortfolioComponentId.IMPACT_PILLAR,
    PortfolioChartsComponent,
    PortfolioBaseType.PORTFOLIO_CHART,
    impactPillarQuery,
    ImpactPillarMapper
  );
  static readonly COUPON_ALLOCATION = new PortfolioComponentConfig(
    PortfolioComponentId.COUPON_ALLOCATION,
    PortfolioChartsComponent,
    PortfolioBaseType.PORTFOLIO_CHART,
    couponAllocationQuery,
    CouponAllocationMapper
  );
  static readonly CURRENCY_BREAKDOWN = new PortfolioComponentConfig(
    PortfolioComponentId.CURRENCY_BREAKDOWN,
    PortfolioChartsComponent,
    PortfolioBaseType.PORTFOLIO_CHART,
    currencyAllocationQuery,
    CurrencyAllocationMapper
  );
  static readonly GEOGRAPHIC_BREAKDOWN = new PortfolioComponentConfig(
    PortfolioComponentId.GEOGRAPHIC_BREAKDOWN,
    PortfolioChartsComponent,
    PortfolioBaseType.PORTFOLIO_CHART,
    geographicAllocationQuery,
    GeographicAllocationMapper
  );
  static readonly MARKET_CAPITALISATION = new PortfolioComponentConfig(
    PortfolioComponentId.MARKET_CAPITALISATION,
    PortfolioChartsComponent,
    PortfolioBaseType.PORTFOLIO_CHART,
    marketCapAllocationQuery,
    MarketCapitalisationMapper
  );
  static readonly MATURITY_ALLOCATION = new PortfolioComponentConfig(
    PortfolioComponentId.MATURITY_ALLOCATION,
    PortfolioChartsComponent,
    PortfolioBaseType.PORTFOLIO_CHART,
    portfolioAllocationFIQuery,
    PortfolioAllocationFIMapper
  );
  // produces array with PORTFOLIO_EXPOSURE and STRATEGY_BREAKDOWN allocations
  static readonly PORTFOLIO_EXPOSURE = new PortfolioComponentConfig(
    PortfolioComponentId.PORTFOLIO_EXPOSURE,
    PortfolioChartsComponent,
    PortfolioBaseType.PORTFOLIO_CHART,
    hedgeAllocationQuery,
    HedgeAllocationMapper
  );
  static readonly PORTFOLIO_RISK_AND_RETURN_ANALYSIS = new PortfolioComponentConfig(
    PortfolioComponentId.PORTFOLIO_RISK_AND_RETURN_ANALYSIS,
    PortfolioChartsComponent,
    PortfolioBaseType.PORTFOLIO_CHART,
    riskReturnAnalysisQuery,
    RiskReturnAnalysisMapper
  );
  static readonly QUALITY_ALLOCATION = new PortfolioComponentConfig(
    PortfolioComponentId.QUALITY_ALLOCATION,
    PortfolioChartsComponent,
    PortfolioBaseType.PORTFOLIO_CHART,
    qualityAllocationQuery,
    QualityAllocationMapper
  );
  static readonly SECTOR_ALLOCATION = new PortfolioComponentConfig(
    PortfolioComponentId.SECTOR_ALLOCATION,
    PortfolioChartsComponent,
    PortfolioBaseType.PORTFOLIO_CHART,
    sectorAllocationQuery,
    SectorAllocationMapper
  );
  static readonly SECURITY_TYPE_EXPOSURE = new PortfolioComponentConfig(
    PortfolioComponentId.SECURITY_TYPE_EXPOSURE,
    PortfolioChartsComponent,
    PortfolioBaseType.PORTFOLIO_CHART,
    securityExposureQuery,
    SecurityExposureMapper
  );

  // produces array with TOP_LONG_EXPOSURES and TOP_SHORT_EXPOSURES allocations
  static readonly TOP_LONG_EXPOSURES = new PortfolioComponentConfig(
    PortfolioComponentId.TOP_LONG_EXPOSURES,
    PortfolioChartsComponent,
    PortfolioBaseType.PORTFOLIO_CHART,
    topExposureQuery,
    TopExposureMapper
  );
  static readonly TOP_TEN_HOLDINGS = new PortfolioComponentConfig(
    PortfolioComponentId.TOP_TEN_HOLDINGS,
    PortfolioChartsComponent,
    PortfolioBaseType.PORTFOLIO_CHART,
    topHoldingsQuery,
    TopHoldingsMapper
  );

  static readonly TOP_TEN_HOLDINGS_MONEY_FUNDS = new PortfolioComponentConfig(
    PortfolioComponentId.TOP_TEN_HOLDINGS_MONEY_FUNDS,
    PortfolioHoldingsComponent,
    PortfolioBaseType.HOLDINGS,
    topTenHoldingsMoneyFundsQuery,
    TopTenHoldingsMoneyFundsMapper,
    'top-ten-holding-money-funds'
  );

  //  custom components
  static readonly PORTFOLIO_STATISTICS_AND_POSITIONS = new PortfolioComponentConfig(
    PortfolioComponentId.PORTFOLIO_STATISTICS_AND_POSITIONS,
    StatisticsAndPositionsComponent,
    PortfolioBaseType.STATISTICS_AND_POSITIONS,
    statisticsQuery,
    StatisticMapper,
    'statistics'
  );

  static readonly HOLDINGS = new PortfolioComponentConfig(
    PortfolioComponentId.HOLDINGS,
    PortfolioHoldingsComponent,
    PortfolioBaseType.HOLDINGS,
    holdingsQuery,
    HoldingsMapper,
    'holdings'
  );

  static readonly MANAGER_ROSTER_ALLOCATION = new PortfolioComponentConfig(
    PortfolioComponentId.MANAGER_ROSTER_ALLOCATION,
    ManagerRosterComponent,
    PortfolioBaseType.MANAGER_ROSTER_ALLOCATION,
    managerRosterAllocationQuery,
    ManagerAllocationMapper,
    'manager'
  );

  static readonly IMPACT_FOCUS = new PortfolioComponentConfig(
    PortfolioComponentId.IMPACT_FOCUS,
    PortfolioChartsComponent,
    PortfolioBaseType.PORTFOLIO_CHART,
    impactFocusQuery,
    ImpactFocusMapper
  );

  static allConfigs: PortfolioComponentConfig[] = [
    PortfolioComponentConfig.ASSET_ALLOCATION,
    PortfolioComponentConfig.COUPON_ALLOCATION,
    PortfolioComponentConfig.CURRENCY_BREAKDOWN,
    PortfolioComponentConfig.HOLDINGS,
    PortfolioComponentConfig.IMPACT_PILLAR,
    PortfolioComponentConfig.GEOGRAPHIC_BREAKDOWN,
    PortfolioComponentConfig.MANAGER_ROSTER_ALLOCATION,
    PortfolioComponentConfig.MARKET_CAPITALISATION,
    PortfolioComponentConfig.MATURITY_ALLOCATION,
    PortfolioComponentConfig.PORTFOLIO_EXPOSURE,
    PortfolioComponentConfig.PORTFOLIO_RISK_AND_RETURN_ANALYSIS,
    PortfolioComponentConfig.PORTFOLIO_STATISTICS_AND_POSITIONS,
    PortfolioComponentConfig.QUALITY_ALLOCATION,
    PortfolioComponentConfig.SECTOR_ALLOCATION,
    PortfolioComponentConfig.SECURITY_TYPE_EXPOSURE,
    PortfolioComponentConfig.TOP_LONG_EXPOSURES,
    PortfolioComponentConfig.TOP_TEN_HOLDINGS,
    PortfolioComponentConfig.TOP_TEN_HOLDINGS_MONEY_FUNDS,
    PortfolioComponentConfig.IMPACT_FOCUS,
  ];

  // returns matching config or undefined
  static getConfig(
    componentId: PortfolioComponentId
  ): PortfolioComponentConfig {
    return this.allConfigs.find(
      (config: PortfolioComponentConfig): boolean =>
        config.componentId === componentId
    ); // just match on key
  }

  // e.g. console.log(PortfolioComponentConfig.PORTFOLIO_STATISTICS_AND_POSITIONS) prints 'PORTFOLIO_STATISTICS_AND_POSITIONS'
  toString() {
    return this.componentId;
  }
}
